import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { pricingPlans, heroSlides } from '@/mocks/homeData';

const AUTO_SLIDE_INTERVAL = 5000;

export default function HeroSection() {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const totalSlides = heroSlides.length;

  const goTo = useCallback((index: number) => {
    if (isTransitioning || index === current) return;
    setIsTransitioning(true);
    setCurrent(index);
    setTimeout(() => setIsTransitioning(false), 600);
  }, [current, isTransitioning]);

  const goNext = useCallback(() => {
    goTo((current + 1) % totalSlides);
  }, [current, totalSlides, goTo]);

  const goPrev = useCallback(() => {
    goTo((current - 1 + totalSlides) % totalSlides);
  }, [current, totalSlides, goTo]);

  useEffect(() => {
    timerRef.current = setInterval(goNext, AUTO_SLIDE_INTERVAL);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [goNext]);

  const handleMouseEnter = () => {
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const handleMouseLeave = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(goNext, AUTO_SLIDE_INTERVAL);
  };

  return (
    <section
      className="relative min-h-screen flex items-center overflow-hidden"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {heroSlides.map((slide, index) => (
        <div
          key={index}
          className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
            index === current ? 'opacity-100 z-10' : 'opacity-0 z-0'
          }`}
        >
          <img
            src={slide.image}
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/60"></div>
        </div>
      ))}

      <div className="relative z-20 w-full mx-auto max-w-7xl px-4 md:px-6 py-28 md:py-36">
        <div className="text-center mb-14 md:mb-20">
          <div className="inline-flex items-center gap-2 px-5 py-2 bg-accent-500/90 backdrop-blur-sm rounded-full mb-8">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-fire-line text-sm text-background-50"></i>
            </div>
            <span className="text-sm text-background-50 font-bold tracking-wide">限时特惠 · 立省高达 ¥6,000</span>
          </div>

          {heroSlides.map((slide, index) => (
            <div
              key={index}
              className={`transition-all duration-500 ease-in-out ${
                index === current
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-4 absolute inset-x-0 top-0 pointer-events-none'
              }`}
            >
              <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl text-background-50 leading-tight mb-6 max-w-4xl mx-auto">
                {slide.title}
              </h1>
              <p className="text-lg md:text-xl text-background-50/75 leading-relaxed max-w-2xl mx-auto mb-10">
                {slide.subtitle}
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/checkout?plan=专业进阶"
                  className="px-8 py-3.5 bg-accent-500 text-background-50 rounded-full font-bold text-base hover:bg-accent-600 transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg shadow-accent-500/30 hover:shadow-accent-500/50 hover:scale-105"
                >
                  {slide.cta}
                </Link>
                <Link
                  to="/courses"
                  className="px-8 py-3.5 bg-background-50/12 text-background-50 rounded-full font-semibold text-base border border-background-50/25 hover:bg-background-50/20 transition-all duration-300 whitespace-nowrap cursor-pointer backdrop-blur-sm"
                >
                  {slide.secondaryLabel}
                  <i className="ri-arrow-right-line ml-2 text-sm"></i>
                </Link>
              </div>
            </div>
          ))}

          <div className="flex items-center justify-center gap-6 mt-12 flex-wrap">
            <div className="flex items-center gap-2 text-background-50/60">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-user-star-line text-sm text-accent-400"></i>
              </div>
              <span className="text-sm">8,000+ 学员信赖</span>
            </div>
            <div className="w-1 h-1 rounded-full bg-background-50/25"></div>
            <div className="flex items-center gap-2 text-background-50/60">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-building-2-line text-sm text-accent-400"></i>
              </div>
              <span className="text-sm">200+ 名企合作</span>
            </div>
            <div className="w-1 h-1 rounded-full bg-background-50/25"></div>
            <div className="flex items-center gap-2 text-background-50/60">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-award-line text-sm text-accent-400"></i>
              </div>
              <span className="text-sm">98% 就业率</span>
            </div>
          </div>

          <div className="flex items-center justify-center gap-3 mt-10">
            {heroSlides.map((_, index) => (
              <button
                key={index}
                onClick={() => goTo(index)}
                className={`rounded-full transition-all duration-300 cursor-pointer ${
                  index === current
                    ? 'w-8 h-2.5 bg-accent-500'
                    : 'w-2.5 h-2.5 bg-background-50/30 hover:bg-background-50/50'
                }`}
                aria-label={`Slide ${index + 1}`}
              />
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 max-w-5xl mx-auto">
          {pricingPlans.map((plan, index) => {
            const isHighlighted = plan.highlighted;

            return (
              <div
                key={plan.name}
                className={`relative rounded-2xl backdrop-blur-sm border transition-all duration-500 hover:scale-[1.02] group ${
                  isHighlighted
                    ? 'bg-accent-500/20 border-accent-400/50'
                    : 'bg-background-50/8 border-background-200/15 hover:border-background-200/30'
                }`}
                style={{
                  animationDelay: `${index * 0.15}s`,
                  animation: 'fadeInUp 0.7s ease-out forwards',
                }}
              >
                {isHighlighted && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 z-10">
                    <span className="px-5 py-1.5 bg-accent-500 text-background-50 text-xs font-bold rounded-full whitespace-nowrap shadow-lg shadow-accent-500/30">
                      最受欢迎
                    </span>
                  </div>
                )}

                <div className="p-6 md:p-8">
                  <h3 className={`font-heading text-xl md:text-2xl mb-1.5 ${
                    isHighlighted ? 'text-accent-400' : 'text-background-50'
                  }`}>
                    {plan.name}
                  </h3>
                  <p className="text-sm text-background-50/45 mb-6">
                    {index === 0 ? '适合零基础入门' : index === 1 ? '适合系统进阶提升' : '适合全面深度学习'}
                  </p>

                  <div className="mb-6">
                    <div className="flex items-baseline gap-1">
                      <span className="text-base text-background-50/55">¥</span>
                      <span className={`font-heading text-5xl font-bold ${
                        isHighlighted ? 'text-accent-400' : 'text-background-50'
                      }`}>
                        {plan.price.toLocaleString()}
                      </span>
                    </div>
                    {plan.originalPrice && (
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className="text-sm text-background-50/35 line-through">
                          ¥{plan.originalPrice.toLocaleString()}
                        </span>
                        <span className="text-xs text-accent-400 font-semibold bg-accent-500/15 px-2 py-0.5 rounded-full">
                          省 ¥{(plan.originalPrice - plan.price).toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>

                  <ul className="space-y-3 mb-8">
                    {plan.features.slice(0, 5).map((feature) => (
                      <li key={feature} className="flex items-start gap-2.5">
                        <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                          <i className={`ri-check-line text-xs ${
                            isHighlighted ? 'text-accent-400' : 'text-background-50/65'
                          }`}></i>
                        </div>
                        <span className="text-sm text-background-50/65 leading-relaxed">{feature}</span>
                      </li>
                    ))}
                    {plan.features.length > 5 && (
                      <li className="text-xs text-background-50/35 pl-6.5">
                        +{plan.features.length - 5} 项更多权益
                      </li>
                    )}
                  </ul>

                  <Link
                    to={`/checkout?plan=${encodeURIComponent(plan.name)}`}
                    className={`block w-full text-center py-3 rounded-full font-semibold text-sm transition-all duration-300 whitespace-nowrap cursor-pointer ${
                      isHighlighted
                        ? 'bg-accent-500 text-background-50 hover:bg-accent-600 shadow-lg shadow-accent-500/25 hover:shadow-accent-500/40'
                        : 'bg-background-50/12 text-background-50 hover:bg-background-50/20 border border-background-50/15'
                    }`}
                  >
                    {isHighlighted ? '立即报名' : '了解详情'}
                  </Link>
                </div>
              </div>
            );
          })}
        </div>

        <div className="text-center mt-10">
          <p className="text-xs text-background-50/35 flex items-center justify-center gap-1.5">
            <i className="ri-shield-check-line"></i>
            下单即表示同意
            <Link to="/terms" className="underline underline-offset-2 hover:text-background-50/60 transition-colors cursor-pointer">
              服务条款
            </Link>
            和
            <Link to="/refund-policy" className="underline underline-offset-2 hover:text-background-50/60 transition-colors cursor-pointer">
              退款政策
            </Link>
          </p>
        </div>
      </div>

      <button
        onClick={goPrev}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-30 w-10 h-10 md:w-12 md:h-12 rounded-full bg-background-50/10 backdrop-blur-sm border border-background-50/20 flex items-center justify-center hover:bg-background-50/20 transition-all duration-300 cursor-pointer group"
        aria-label="Previous slide"
      >
        <i className="ri-arrow-left-s-line text-xl text-background-50 group-hover:scale-110 transition-transform"></i>
      </button>
      <button
        onClick={goNext}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-30 w-10 h-10 md:w-12 md:h-12 rounded-full bg-background-50/10 backdrop-blur-sm border border-background-50/20 flex items-center justify-center hover:bg-background-50/20 transition-all duration-300 cursor-pointer group"
        aria-label="Next slide"
      >
        <i className="ri-arrow-right-s-line text-xl text-background-50 group-hover:scale-110 transition-transform"></i>
      </button>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}